import type { AdRecommendation, AdVerdict } from '@nono/contract';
import type { Locale } from './i18n';

/*
 * VERDICT RENDERING — the client half of the per-ad keep/kill/scale engine.
 * The server (apps/api/src/dashboard/recommend.ts) emits verdict + confidence +
 * reason CODES with numeric params; the localized sentences live here, in the
 * same nb/en pattern as the rest of the app. Adding a server reason code
 * without a template here falls back to showing the raw code (never crashes).
 */

export const VERDICT_META: Record<
  AdVerdict,
  { nb: string; en: string; tone: 'good' | 'warn' | 'bad' | 'neutral' | 'brand' }
> = {
  scale: { nb: 'Skaler', en: 'Scale', tone: 'good' },
  keep: { nb: 'Behold', en: 'Keep', tone: 'brand' },
  iterate: { nb: 'Forbedre kreativ', en: 'Improve creative', tone: 'warn' },
  watch: { nb: 'La den lære', en: 'Let it learn', tone: 'neutral' },
  kill: { nb: 'Skru av', en: 'Turn off', tone: 'bad' },
  inactive: { nb: 'Inaktiv', en: 'Inactive', tone: 'neutral' },
};

export const CONFIDENCE_LABEL: Record<AdRecommendation['confidence'], { nb: string; en: string }> =
  {
    high: { nb: 'høy sikkerhet', en: 'high confidence' },
    medium: { nb: 'middels sikkerhet', en: 'medium confidence' },
    low: { nb: 'lav sikkerhet', en: 'low confidence' },
  };

const TEMPLATES: Record<string, { nb: string; en: string }> = {
  INACTIVE_HISTORY: {
    nb: 'Ingen visninger siste {daysSinceLastImpression} dager — vises som historikk, ikke et råd. Siste kost: {cpx} kr per {outcomeNoun}.',
    en: 'No impressions for {daysSinceLastImpression} days — shown as history, not advice. Last cost: {cpx} NOK per {outcomeNoun}.',
  },
  LOW_DATA: {
    nb: 'For lite data ({spend} kr, {impressions} visninger, {daysActive} dager aktiv) — under minstekravet for en dom.',
    en: 'Not enough data ({spend} NOK, {impressions} impressions, {daysActive} days active) — below the evidence floor for any verdict.',
  },
  LEARNING_MILESTONE: {
    nb: 'La den lære. Vurder på nytt ved {milestoneSpend} kr forbruk, første {outcomeNoun} eller 14 dager — det som kommer først.',
    en: 'Let it learn. Re-judge at {milestoneSpend} NOK spend, the first {outcomeNoun}, or 14 days — whichever comes first.',
  },
  ZERO_RESULTS_SPEND: {
    nb: '{spend} kr brukt uten én eneste {outcomeNoun}. Ved breakeven ({breakeven} kr) skulle den levert ~{expectedOutcomes} — sjansen for null er under 5 %. Skru av.',
    en: '{spend} NOK spent with zero {outcomeNoun}s. At breakeven ({breakeven} NOK) it should have produced ~{expectedOutcomes} — under a 5% chance of zero. Turn it off.',
  },
  EARLY_CREATIVE_FAIL: {
    nb: 'Ingen {outcomeNoun} etter {spend} kr, og kreativen viser ingen trekkraft — fiks den nå i stedet for å vente på kill-grensen ({milestoneSpend} kr).',
    en: 'No {outcomeNoun}s after {spend} NOK and the creative shows no pull — fix it now instead of waiting for the kill threshold ({milestoneSpend} NOK).',
  },
  BAD_ECON: {
    nb: '{cpx} kr per {outcomeNoun} er over 3× mål ({anchor} kr) med {outcomes} {outcomeNoun}er bak tallet — budsjettet gjør mer nytte i andre annonser.',
    en: '{cpx} NOK per {outcomeNoun} is above 3× target ({anchor} NOK) with {outcomes} {outcomeNoun}s behind the number — the budget works harder in other ads.',
  },
  BUDGET_HOG_KILL: {
    nb: 'Spiser {spendSharePct} % av budsjettet til {cpx} kr per {outcomeNoun} ({anchorMultiple}× mål). På et fast budsjett er dette den dyreste annonsen å beholde.',
    en: 'Eating {spendSharePct}% of budget at {cpx} NOK per {outcomeNoun} ({anchorMultiple}× target). On a fixed budget this is the most expensive ad to keep.',
  },
  BUDGET_HOG: {
    nb: 'Tar {spendSharePct} % av budsjettet med middelmådig økonomi ({cpx} kr per {outcomeNoun}, {anchorMultiple}× mål) — flytt budsjett til vinnerne og forbedre kreativen.',
    en: 'Takes {spendSharePct}% of budget at mediocre economics ({cpx} NOK per {outcomeNoun}, {anchorMultiple}× target) — shift budget to the winners and fix the creative.',
  },
  BEST_OF_WEAK_FIELD: {
    nb: 'Ingen annen aktiv annonse i samme trakt under 1,8× mål å flytte budsjettet til — dette er den beste i et svakt felt. Fiks kreativen før du omfordeler; å pause den nå stopper all leveranse.',
    en: 'No other active same-funnel ad under 1.8× target to receive the budget — this is the best of a weak field. Fix the creative before reallocating; pausing it now halts delivery entirely.',
  },
  FATIGUE_REFRESH: {
    nb: 'Frekvens {frequencyLate} og CTR ned {ctrDropPct} % i siste halvdel ({ctrEarly} % → {ctrLate} %) — publikummet er i ferd med å mettes. Lag ny variant FØR CPA stiger.',
    en: 'Frequency {frequencyLate} and CTR down {ctrDropPct}% in the latter half ({ctrEarly}% → {ctrLate}%) — the audience is saturating. Ship a fresh variant BEFORE CPA rises.',
  },
  OUTCOME_DECAY: {
    nb: 'Annonsen døde midt i perioden: {primaryFirstHalf} {outcomeNoun}er i første halvdel, NULL i andre — tross {spendSecondHalf} kr brukt. Snittet ({cpx} kr per {outcomeNoun}) skjuler det. Lag ny variant nå.',
    en: 'The ad died mid-range: {primaryFirstHalf} {outcomeNoun}s in the first half, ZERO in the second — despite {spendSecondHalf} NOK spent. The average ({cpx} NOK per {outcomeNoun}) hides it. Ship a fresh variant now.',
  },
  KILL_WATCH: {
    nb: 'Kill-vakt: andre halvdel alene ({spendSecondHalf} kr, null {outcomeNoun}er) passerer den statistiske kill-grensen ({zeroKillSpend} kr). Refresh umiddelbart — gir ikke refreshen resultater, skru av.',
    en: 'Kill-watch: the second half alone ({spendSecondHalf} NOK, zero {outcomeNoun}s) clears the statistical kill line ({zeroKillSpend} NOK). Refresh immediately — if the refresh does not produce, turn it off.',
  },
  WINNER_FATIGUING: {
    nb: 'Økonomien er fortsatt god ({cpx} kr per {outcomeNoun}), men signalene peker ned — refresh en vinner mens den ennå vinner.',
    en: 'Economics are still good ({cpx} NOK per {outcomeNoun}) but the signals point down — refresh a winner while it is still winning.',
  },
  SCALE_WINNER: {
    nb: '{cpx} kr per {outcomeNoun} er på/under mål ({anchor} kr) med {outcomes} {outcomeNoun}er — øk budsjettet 20–30 % og sjekk igjen om 3–4 dager.',
    en: '{cpx} NOK per {outcomeNoun} at/below target ({anchor} NOK) with {outcomes} {outcomeNoun}s — raise budget 20–30% and re-check in 3–4 days.',
  },
  RAISE_TOTAL_BUDGET: {
    nb: 'Allerede {spendSharePct} % av budsjettet — skalering her betyr å øke totalbudsjettet, ikke å flytte fra andre annonser.',
    en: 'Already {spendSharePct}% of budget — scaling this means raising the total budget, not reallocating from other ads.',
  },
  PROMISING_THIN: {
    nb: 'Sterk økonomi ({cpx} kr per {outcomeNoun}), men bare {outcomes} {outcomeNoun}er — behold, og skaler først ved 3+.',
    en: 'Strong economics ({cpx} NOK per {outcomeNoun}) but only {outcomes} {outcomeNoun}s — keep, and scale only at 3+.',
  },
  KEEP_PROFITABLE: {
    nb: '{cpx} kr per {outcomeNoun} er innenfor breakeven første måned ({breakeven} kr) — og et medlemskap er verdt ~6× det på LTV. La den gå.',
    en: '{cpx} NOK per {outcomeNoun} is within first-month breakeven ({breakeven} NOK) — and a membership is worth ~6× that on LTV. Let it run.',
  },
  PEER_LAGGARD: {
    nb: '{cpx} kr per {outcomeNoun} mot kontomedian {medianCpx} kr — klart svakest i flokken.',
    en: '{cpx} NOK per {outcomeNoun} vs account median {medianCpx} NOK — clearly the weakest of the pack.',
  },
  ABOVE_BREAKEVEN: {
    nb: '{cpx} kr per {outcomeNoun} er over breakeven første måned ({breakeven} kr), men under kill-grensen — LTV (~6×) bærer den så vidt. Forbedre kreativen.',
    en: '{cpx} NOK per {outcomeNoun} is above first-month breakeven ({breakeven} NOK) but below the kill line — LTV (~6×) barely carries it. Improve the creative.',
  },
  FIX_HOOK: {
    nb: 'Hook-rate {hookRate} % (mål ≥ 25 %) — de første 3 sekundene stopper ingen. Ny åpning: ansikt, påstand eller resultat i bilde 1.',
    en: 'Hook rate {hookRate}% (target ≥ 25%) — the first 3 seconds stop no one. New opener: a face, a claim, or a result in frame 1.',
  },
  FIX_MIDDLE: {
    nb: 'God åpning (hook {hookRate} %), men midten lekker: hold-rate {holdRate} %, p50 {p50} % — kort ned videoen og kom raskere til poenget.',
    en: 'Good opener (hook {hookRate}%) but the middle leaks: hold rate {holdRate}%, p50 {p50}% — cut the video down and get to the point faster.',
  },
  FIX_CTA: {
    nb: 'Folk ser ({thruplayRate} % thruplay), men klikker ikke (link-CTR {linkCtr} %) — problemet er tilbudet/CTA-en, ikke videoen.',
    en: 'People watch ({thruplayRate}% thruplay) but do not click (link CTR {linkCtr}%) — the offer/CTA is the problem, not the video.',
  },
  NEW_CONCEPT: {
    nb: 'Svak respons (engasjement {engagementRate} %, link-CTR {linkCtr} %) — ikke juster detaljer, lag et nytt konsept.',
    en: 'Weak response (engagement {engagementRate}%, link CTR {linkCtr}%) — do not tweak, build a new concept.',
  },
  EST_ROAS_MODELED: {
    nb: 'Estimert ROAS {estRoas}× er modellert ({outcomes} × {valuePerOutcome} kr blandet verdi ÷ {spend} kr) — ikke målt inntekt per annonse.',
    en: 'Estimated ROAS {estRoas}× is modeled ({outcomes} × {valuePerOutcome} NOK blended value ÷ {spend} NOK) — not measured per-ad revenue.',
  },
  ANCHOR_FALLBACK: {
    nb: 'Målestokken er myk (mål-CPB/CPL mangler eller verdien er beregnet) — dømt mot {anchor} kr. Tilliten er nedjustert.',
    en: 'The yardstick is soft (target CPB/CPL missing or the value is modeled) — judged against {anchor} NOK. Confidence downgraded.',
  },
  THIN_EVIDENCE: {
    nb: 'Ser dyr ut ({cpx} kr per {outcomeNoun}), men {outcomes} {outcomeNoun}er er for lite til å dømme — vurder på nytt ved {milestoneSpend} kr.',
    en: 'Looks expensive ({cpx} NOK per {outcomeNoun}) but {outcomes} {outcomeNoun}s is too little to judge — re-judge at {milestoneSpend} NOK.',
  },
  DATA_INCONCLUSIVE: {
    nb: 'Ingen regel traff entydig — behandles som læring. Sjekk igjen om noen dager.',
    en: 'No rule matched cleanly — treated as learning. Check back in a few days.',
  },
};

/** Localize the {outcomeNoun} param ('booking' | 'lead'). */
const OUTCOME_NOUN: Record<string, Record<Locale, string>> = {
  booking: { nb: 'booking', en: 'booking' },
  lead: { nb: 'lead', en: 'lead' },
};

function fmtParam(v: number | string | null, locale: Locale): string {
  if (v == null) return '—';
  if (typeof v === 'number') {
    return new Intl.NumberFormat(locale === 'nb' ? 'nb-NO' : 'en-GB', {
      maximumFractionDigits: 2,
    }).format(v);
  }
  return v;
}

/** Render one reason code + params to a localized sentence. Unknown code → raw code. */
export function renderReason(
  reason: { code: string; params: Record<string, number | string | null> },
  locale: Locale,
): string {
  const tpl = TEMPLATES[reason.code];
  if (!tpl) return reason.code;
  return tpl[locale].replace(/\{(\w+)\}/g, (_, key: string) => {
    const v = reason.params[key];
    if (key === 'outcomeNoun' && typeof v === 'string') return OUTCOME_NOUN[v]?.[locale] ?? v;
    return fmtParam(v ?? null, locale);
  });
}
